/** Firestore `stats/{teamId}/...` team ids for admin tools (stat tracker, line builder, etc.). */
/** Coaches are used by Gladiator Gazette AI — keep in sync with `TEAM_RECAP_COACHES` in functions/index.js. */

export const TEAM_STATS_TEAMS = [
  {
    label: '12U',
    teamId: 'glass-city-gladiators-12u',
    coaches: [
      { name: 'Duncan Vail', role: 'Head Coach' },
      { name: 'Jeff Seiler', role: 'Assistant Coach' },
      { name: 'Rusty Nighbert', role: 'Team Director' },
    ],
  },
  {
    label: '14U',
    teamId: 'glass-city-gladiators-14u',
    coaches: [
      { name: 'Mickey Katz', role: 'Head Coach' },
      { name: 'James Otting', role: 'Offense Coach' },
      { name: 'Jason Birkle', role: 'Defense Coach' },
    ],
  },
];

export const DEFAULT_TEAM_STATS_TEAM_ID = TEAM_STATS_TEAMS[0].teamId;

/**
 * Firestore + Storage root segment for hockey stats / Gazette.
 * Production builds always use `stats`.
 * Local `npm start` (NODE_ENV=development) uses `statsDev` so test games and Gazettes
 * never appear on the production site, Live Scoreboard, or public Gazette.
 *
 * Override with REACT_APP_STATS_ROOT=stats or statsDev if you need to point local at prod data
 * (use carefully — writes go to that root).
 *
 * Admin Portal can pass an explicit root when cleaning production from a local session —
 * Stat Tracker / public pages always use this helper (never an Admin-only override).
 */
export function getStatsCollectionRoot() {
  const override = typeof process !== 'undefined' ? process.env.REACT_APP_STATS_ROOT : '';
  if (override === 'stats' || override === 'statsDev') return override;
  return process.env.NODE_ENV === 'development' ? 'statsDev' : 'stats';
}

/** True when local npm start can choose between sandbox and production stats roots in Admin. */
export function canAdminChooseStatsRoot() {
  return process.env.NODE_ENV === 'development';
}

/** True when the app is reading/writing the local sandbox root (`statsDev`). */
export function isStatsSandbox() {
  return getStatsCollectionRoot() === 'statsDev';
}

/** True if `teamId` is one of {@link TEAM_STATS_TEAMS}. */
export function isKnownTeamStatsTeamId(teamId) {
  if (typeof teamId !== 'string' || !teamId.trim()) return false;
  const t = teamId.trim();
  return TEAM_STATS_TEAMS.some((row) => row.teamId === t);
}

/** Returns `teamId` when known, otherwise `fallback` (defaults to first team). */
export function normalizeTeamStatsTeamId(teamId, fallback = DEFAULT_TEAM_STATS_TEAM_ID) {
  if (typeof teamId !== 'string' || !teamId.trim()) return fallback;
  const t = teamId.trim();
  return isKnownTeamStatsTeamId(t) ? t : fallback;
}

/** Coaches for Gazette quotes for a known team id (empty if unknown). */
export function getTeamStatsCoaches(teamId) {
  const row = TEAM_STATS_TEAMS.find((t) => t.teamId === teamId);
  return Array.isArray(row?.coaches) ? row.coaches : [];
}

/** Age-group label (e.g. 12U) for a known team id. */
export function getTeamStatsLabel(teamId) {
  return TEAM_STATS_TEAMS.find((t) => t.teamId === teamId)?.label || '';
}
